import { useCallback, useEffect, useRef } from "react";

const OTP_CODE_PATTERN = /^\d{6}$/;

type WebOtpCredential = Credential & { code: string };

type UseWebOtpOptions = {
  enabled: boolean;
  onCode: (code: string) => void;
};

/**
 * Listens for an origin-bound SMS OTP when the browser supports WebOTP.
 * The caller must still provide a normal input: this API is intentionally a
 * progressive enhancement and is unavailable in many browsers.
 */
export default function useWebOtp({ enabled, onCode }: UseWebOtpOptions) {
  const abortControllerRef = useRef<AbortController | null>(null);
  const onCodeRef = useRef(onCode);

  useEffect(() => {
    onCodeRef.current = onCode;
  }, [onCode]);

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const start = useCallback(() => {
    if (abortControllerRef.current) {
      return;
    }

    if (
      !window.isSecureContext ||
      !navigator.credentials?.get ||
      typeof AbortController === "undefined"
    ) {
      return;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    void navigator.credentials
      .get({
        otp: { transport: ["sms"] },
        signal: abortController.signal,
      } as CredentialRequestOptions)
      .then((credential) => {
        const code = (credential as WebOtpCredential | null)?.code;
        if (!abortController.signal.aborted && code && OTP_CODE_PATTERN.test(code)) {
          onCodeRef.current(code);
        }
      })
      // AbortError is the expected result when the user closes the dialog or
      // requests another OTP. Other failures should retain the manual flow.
      .catch(() => undefined)
      .finally(() => {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      });
  }, []);

  useEffect(() => {
    if (enabled) {
      start();
    } else {
      stop();
    }
  }, [enabled, start, stop]);

  useEffect(() => {
    return stop;
  }, [stop]);

  return { start, stop };
}
